import { useCallback, useState } from "react";
import { FormikErrors, FormikTouched } from "formik";
import { useStores } from "stores/setup/use-store";
import { scrollToFirstError } from "../util/scrollToFirstError";

export const useAlertSteps = (totalSteps: number) => {
  const [currentStep, setCurrentStep] = useState(0);
  const {
    dataStores: { insightAlertStore },
  } = useStores();

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const goToStep = useCallback(
    (step: number) => {
      setCurrentStep(Math.max(0, Math.min(step, totalSteps - 1)));
    },
    [totalSteps],
  );

  const resetSteps = useCallback(() => {
    setCurrentStep(0);
  }, []);

  const validateCurrentStep = async (
    step: number,
    validateForm: () => Promise<FormikErrors<any>>,
    setTouched: (touched: FormikTouched<any>, shouldValidate?: boolean) => void,
    setFieldError: (field: string, message: string | undefined) => void,
    values?: any,
  ) => {
    const isInsightMode = insightAlertStore.alertCategory === "insight";

    // For standalone mode: if location type not selected, mark fields as touched to show error
    if (step === 0 && !isInsightMode && !insightAlertStore.alertLocationType) {
      setTouched({ geofence: true, stationRegion: true });
      return false;
    }

    const formErrors = await validateForm();
    const stepFields = getStepFields(step, isInsightMode);
    const stepErrors: Record<string, boolean> = {};

    // In Permissive mode, conditionGroups are optional — skip Yup errors for them
    const isPermissive = values?.restrictionType === false;
    const fieldsToSkipInPermissive = ["conditionGroups", "templateType"];

    stepFields.forEach((field) => {
      if (formErrors[field]) {
        if (isPermissive && fieldsToSkipInPermissive.includes(field)) return;
        stepErrors[field] = true;
      }
    });

    // Manual validation for templateType on step 1 (risk selection)
    // In Permissive mode (restrictionType === false), templateType is auto-set to "no_category"
    // so we only validate in Restrictive mode (restrictionType !== false)
    if (step === 1 && stepFields.includes("templateType")) {
      const templateType = values?.templateType;
      const isPermissive = values?.restrictionType === false;
      if (!isPermissive && (!templateType || templateType === "")) {
        stepErrors["templateType"] = true;
        // Set the error message manually so it shows in the UI
        setFieldError("templateType", "pleaseSelectRisk");
      }
    }

    // Manual validation for conditionGroups on step 1 (conditions)
    // In Permissive mode (restrictionType === false), conditions are optional
    if (step === 1 && stepFields.includes("conditionGroups")) {
      const conditionGroups = values?.conditionGroups;
      const isPermissive = values?.restrictionType === false;
      if (!isPermissive && (!conditionGroups || conditionGroups.length === 0)) {
        stepErrors["conditionGroups"] = true;
        setFieldError("conditionGroups", "pleaseAddCondition");
      }
    }

    // Validate sensor alert condition values on step 1
    if (step === 1 && stepFields.includes("sensorAlertConfig")) {
      const sensorConfig = values?.sensorAlertConfig;
      if (sensorConfig?.sensors?.length > 0 && formErrors["sensorAlertConfig"]) {
        stepErrors["sensorAlertConfig"] = true;
      }
    }

    if (Object.keys(stepErrors).length > 0) {
      const touchedFields: Record<string, boolean> = {};
      // Mark all step fields as touched
      stepFields.forEach((field) => {
        touchedFields[field] = true;
      });
      // Also mark any fields with errors as touched
      Object.keys(stepErrors).forEach((field) => {
        touchedFields[field] = true;
      });
      setTouched(touchedFields, false); // false to not trigger validation again

      setTimeout(() => {
        scrollToFirstError(formErrors, touchedFields);
      }, 100);

      return false;
    }

    return true;
  };

  const getStepFields = (step: number, isInsightMode: boolean): string[] => {
    // Step 0: Location (same for both modes)
    if (step === 0) {
      if (isInsightMode) {
        // For insight/event mode, validate location selection
        // Location is validated via the location-validation test in the schema
        // Return empty array to let the schema test handle it
        const locationType = insightAlertStore.alertLocationType;

        if (locationType === "area") {
          return ["geofence"];
        } else if (locationType === "point") {
          return ["macroRegionId", "stationRegion"];
        }
        // No location type selected yet - return geofence to trigger location error
        return ["geofence"];
      }

      // For standalone mode, validate name and location fields
      const baseFields = ["name"];
      const locationType = insightAlertStore.alertLocationType;

      if (locationType === "area") {
        baseFields.push("geofence");
      } else if (locationType === "point") {
        baseFields.push("macroRegionId", "stationRegion");
      }

      return baseFields;
    }

    // Step 1+ depends on the mode and total steps
    if (isInsightMode) {
      // Alert creation (insight mode, 2 steps): Step 0 = Location, Step 1 = Delivery/Preferences
      // Event creation (insight mode, 3 steps): Step 0 = Location, Step 1 = Conditions, Step 2 = Preferences
      if (totalSteps === 3) {
        // Event creation flow
        const stepFieldsMap: Record<number, string[]> = {
          1: ["templateType", "conditionGroups", "sensorAlertConfig"],
          2: ["name", "dashboardId"],
        };
        return stepFieldsMap[step] || [];
      }
      // Alert creation flow (2 steps)
      const stepFieldsMap: Record<number, string[]> = {
        1: ["name"],
      };
      return stepFieldsMap[step] || [];
    }

    // Standalone mode: Step 0 = Location, Step 1 = Risk & Conditions, Step 2 = Delivery
    const stepFieldsMap: Record<number, string[]> = {
      1: ["templateType", "conditionGroups"],
      2: ["name"],
    };

    return stepFieldsMap[step] || [];
  };

  return {
    currentStep,
    nextStep,
    prevStep,
    goToStep,
    resetSteps,
    validateCurrentStep,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === totalSteps - 1,
  };
};
